import {
  InsuranceType,
  ProductBackendName,
} from "Components/Features/CheckPrice/Products/productTypes";

import { StyledAlert } from "App/global-style";
import { CoverageComparison } from "Components/CoverageComparison";
import { CoverageCard } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/CoverageCard";
import { Data } from "Components/TextContext/textInterfaces";
import { useFormikContext } from "formik";
import React from "react";
import { InsuranceCoverage } from "Store/interfaces/products/coverage";
import {
  coveragePriceDifference,
  InsuranceTypeVariantVariable,
} from "./coverages.helpers";
import { CoveragePriceCalculation } from "./coverages.types";

type PriceFormCoverageValues = { coverage: InsuranceCoverage };

interface Props<TFormValues extends PriceFormCoverageValues> {
  readonly coverages: CoveragePriceCalculation[];
  readonly coverageStandards: InsuranceCoverage[];
  readonly priceFilterValue?: string;
  readonly fieldName: keyof TFormValues;
  readonly product: ProductBackendName;
  readonly priceIsLoading?: boolean;
  readonly textComponents: Data[];
  readonly alertLabel?: string;
  readonly canOnlyBuyKasko?: boolean;
  readonly extensions?: Record<string, InsuranceCoverage>;
  readonly disabledCoverages?: Record<string, React.ReactNode>;
  readonly doNotShow?: InsuranceType;
  readonly insuranceTypeVariant?: InsuranceTypeVariantVariable;
}

export const MultipleProductCoverages = ({
  coverages,
  coverageStandards,
  priceFilterValue,
  fieldName,
  product,
  priceIsLoading,
  textComponents,
  alertLabel,
  canOnlyBuyKasko,
  extensions = {},
  disabledCoverages = {},
  doNotShow,
  insuranceTypeVariant,
}: Props<PriceFormCoverageValues>): React.ReactElement => {
  const formProps = useFormikContext<PriceFormCoverageValues>();
  const includesCoverage = ([coverage]: CoveragePriceCalculation): boolean =>
    coverageStandards.includes(coverage);

  const sortedProductCoverages = coverages
    .filter(includesCoverage)
    .filter(([coverage]) => !Object.values(extensions).includes(coverage))
    .sort(coveragePriceDifference);

  const selectedCoverage: InsuranceCoverage = formProps.values[fieldName];

  return (
    <>
      {sortedProductCoverages.map(([coverage, priceCalculationEntry]) => {
        const isDisabledCoverage =
          Object.keys(disabledCoverages).includes(coverage);
        return (
          <CoverageCard
            key={coverage}
            fieldName={fieldName}
            product={product}
            textComponents={textComponents}
            coverage={coverage}
            prices={priceCalculationEntry.prices}
            priceFilterValue={priceFilterValue}
            hideRadioButton={isDisabledCoverage}
            disabled={isDisabledCoverage}
            disabledCoverageDesc={disabledCoverages[coverage] ?? undefined}
            priceIsLoading={priceIsLoading}
            extendedCoverage={coverages.find(
              ([c]) => c === extensions[coverage],
            )}
            doNotShow={doNotShow}
            insuranceTypeVariant={insuranceTypeVariant}
          />
        );
      })}
      {sortedProductCoverages.length > 0 && (
        <CoverageComparison
          productBackendName={product}
          selectedCoverage={selectedCoverage}
          doNotShow={doNotShow}
        />
      )}
      {canOnlyBuyKasko && coverages.length > 0 && alertLabel && (
        <StyledAlert title={alertLabel} />
      )}
    </>
  );
};
